/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { GridConfig } from '@coursera-web/cds-labs';

import { isRightToLeft } from 'js/lib/language';

import { Button, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import { BasicCard, ShowMoreGridSection } from 'bundles/cds-labs/components';
import ResponsiveImage from 'bundles/cds-labs/components/Modules/HeroBentoModule/ResponsiveImage';
import ModuleLayout, { noImage } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import type { ModuleLayoutConfig } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import Eyebrow from 'bundles/cds-labs/components/Modules/components/Eyebrow';
import Image, { type ImageType } from 'bundles/cds-labs/components/Modules/components/Image';
import PreservedUtmLink from 'bundles/cds-labs/components/Modules/components/PreservedUtmLink';
import { getColorFromColorName, isInvertedColor } from 'bundles/cds-labs/components/Modules/utils/colors';

import _t from 'i18n!nls/cds-labs';

const CARD_SIZE = { SMALL: 'small', REGULAR: 'regular', LARGE: 'large' } as const;
const XXS_BREAKPOINT = 450; // pixel width for extra extra small screens

export type CardSize = (typeof CARD_SIZE)[keyof typeof CARD_SIZE];
export type ColorChoice =
  | 'blue'
  | 'white'
  | 'black'
  | 'light-blue'
  | 'dark-blue'
  | 'grey'
  | 'dark-red'
  | 'primary-blue-xstrong';

type VariantType = 'grid' | 'list';

export type VariantConfig = {
  sm?: VariantType;
};

// The offset is half of the width of the card
const BACKGROUND_IMAGE_LEFT_OFFSET = 335;

export type CannedCollectionModuleCard = {
  id: string;
  headline?: string;
  body?: string;
  target?: string;
  ctaText?: string;
  cardBackgroundImage?: ImageType;
  mobileCardBackgroundImage?: ImageType;
  eyebrowImageHeight?: number;
  eyebrow?: string;
  eyebrowBackground?: string;
  eyebrowImage?: ImageType;
  backgroundColor?: ColorChoice;
  onClick?: () => void;
  ctaVariant?: ButtonProps['variant'];
  openInNewTab?: boolean;
};

type Props = {
  heading?: string;
  subheading?: string;
  cards: CannedCollectionModuleCard[];
  size: CardSize;
  color: ColorChoice;
  preloadImage?: boolean;
  lazyLoadImage?: boolean;
  showAllEntries?: boolean;
  showImageInFull?: boolean;
  variantConfig?: VariantConfig;
} & Pick<ModuleLayoutConfig, 'backgroundColor' | 'removeTopPadding' | 'removeBottomPadding'>;

const getCtaConfig = ({
  ctaVariant: inputVariant,
  isDarkBackground,
  isMobile,
  size,
}: {
  ctaVariant: ButtonProps['variant'] | undefined;
  isDarkBackground: boolean;
  isMobile: boolean;
  size: CardSize;
}): { variant: ButtonProps['variant']; iconColor: 'interactive' | 'invert'; isGhostCta: boolean } => {
  let variant = inputVariant ?? (isDarkBackground ? 'secondary' : 'primary');

  if (isDarkBackground && variant === 'ghost') {
    variant = 'ghostInvert';
  }

  if (isMobile && size === CARD_SIZE.LARGE && variant?.includes('ghost')) {
    return { variant: 'secondary', iconColor: 'interactive', isGhostCta: false };
  }

  const isSecondaryLike = variant?.includes('secondary');
  const isGhostCta = !!variant?.includes('ghost');
  const isGhostOnLight = !isDarkBackground && isGhostCta;
  const iconColor = isSecondaryLike || isGhostOnLight ? 'interactive' : 'invert';

  return { variant, iconColor, isGhostCta };
};

const styles = {
  card: css`
    color-scheme: light only;
    display: block;
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: var(--cds-spacing-200);
    text-decoration: none;
    overflow: hidden;

    &:active,
    &:hover,
    &:focus {
      text-decoration: none;
    }
  `,

  largeCardMd: css`
    ${breakpoints.up('md')} {
      border-radius: var(--cds-spacing-300);
    }
  `,

  cardSizeLarge: css`
    padding: var(--cds-spacing-400) var(--cds-spacing-300);
    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-300);
    }
  `,

  cardSizeRegular: css`
    padding: var(--cds-spacing-300);
  `,

  cardSizeSmall: css`
    padding: var(--cds-spacing-200);
  `,

  cardColorBlack: css`
    background-color: var(--cds-color-neutral-primary);
    border: none;
  `,

  cardColorBlue: css`
    background-color: var(--cds-color-emphasis-primary-background-xweak);
    border: none;
  `,

  cardColorWhite: css`
    background-color: var(--cds-color-neutral-background-primary);
    border: 1px solid var(--cds-color-neutral-disabled-strong);
  `,

  cardTitleLarge: css`
    margin-bottom: var(--cds-spacing-50);
    margin-top: var(--cds-spacing-150);
    ${breakpoints.down('sm')} {
      margin-top: var(--cds-spacing-100);
    }
  `,

  cardTitle: css`
    margin-bottom: 2px;
  `,

  eyebrowSpacing: css`
    margin-bottom: var(--cds-spacing-150);
  `,

  eyebrowSpacingLarge: css`
    margin-bottom: 0;
  `,

  cta: css`
    display: flex;
    align-items: center;
    margin-top: var(--cds-spacing-150);
  `,

  ctaLarge: css`
    margin-top: var(--cds-spacing-100);
  `,

  ghostCta: css`
    padding-left: 0;
    padding-right: 0;

    &:hover,
    &:focus-visible {
      background-color: transparent;
      text-decoration: underline;
    }
  `,

  /*
   * "Stretched link" pattern — the heading link's ::before pseudo-element covers the
   * entire card. position: static !important ensures no ancestor repositions the link,
   * keeping the overlay anchored to the card's position: relative container.
   * See https://inclusive-components.design/cards/#:~:text=grid%2Dgap%3A%204.5rem%3B%0A%20%20%7D%0A%7D-,Calls%20to%20action,-As%20I%20said
   */
  stretchedOverlay: css`
    position: static !important;

    &::before {
      content: '';
      cursor: pointer;
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      z-index: 0;
      width: 100%;
      height: 100%;
    }
  `,

  headingLink: css`
    color: inherit;
    text-decoration: none;
    outline: none;

    &:hover,
    &:focus,
    &:active {
      color: inherit;
      text-decoration: none;
    }
  `,

  /* Raise the decorative CTA above the stretched overlay so it receives hover styles natively */
  decorativeCta: css`
    position: relative;
    z-index: 1;
  `,

  /*
   * When the heading link has focus, show the focus ring on the CTA instead.
   * See https://inclusive-components.design/cards/#callstoaction and its demo as an example.
   */
  cardInteractionProxy: css`
    &:focus-within > div:last-child button {
      box-shadow: 0 0 0 2px var(--cds-color-interactive-primary),
        0 0 0 3px var(--cds-color-interactive-stroke-primary-focus-invert);
    }
  `,

  cardContentWrapper: css`
    width: 100%;
    height: 100%;
  `,
  cardContent: (setMaxWidth: boolean) => css`
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    max-width: ${setMaxWidth ? `${BACKGROUND_IMAGE_LEFT_OFFSET}px` : 'unset'};
    box-sizing: border-box;
  `,

  backgroundImage: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    pointer-events: none;
  `,

  backgroundImageInner: (offset: number, isRtl: boolean, isSingleCard: boolean = false) => css`
    position: absolute;
    bottom: 0;
    ${isSingleCard ? 'right: 0;' : `left: ${offset}px;`}

    ${breakpoints.down('sm')} {
      ${isSingleCard && 'right: unset;'}
      ${isSingleCard && `left: ${offset}px;`}
    }

    img {
      ${isRtl ? `transform: scaleX(-1);` : ''};
    }
  `,

  // cancel the y-padding since the block already provides y-padding
  showMoreGridSection: css`
    padding-top: 0;
    padding-bottom: 0;
  `,

  subheading: css`
    margin-top: var(--cds-spacing-50);
  `,

  headingContainer: css`
    margin-bottom: var(--cds-spacing-150);
  `,

  backgroundImageContainer: css`
    pointer-events: none;
    ${breakpoints.up('md')} {
      position: absolute;
      bottom: 0;
      right: 0;
      width: calc(50% - var(--cds-spacing-100));
      height: 100%;
    }
  `,

  backgroundImageFull: css`
    display: flex;
    height: 100%;
    flex-direction: column;
    flex-grow: 1;
    justify-content: end;

    /*
     * Override styles from LazyImgix
     * Beginning of override
     */
    > div,
    > div > div {
      height: 100%;
    }

    > div > div {
      padding-bottom: unset;
    }

    .lazyload-wrapper {
      display: flex;
      height: 100%;
      flex-direction: column;
      flex-grow: 1;
      justify-content: end;
    }

    /* End of override */

    img {
      object-fit: contain;
      object-position: right bottom;
      max-width: unset !important;
      max-height: unset !important;
    }

    ${breakpoints.up('md')} {
      img {
        height: 100% !important;
      }
    }

    ${breakpoints.down('sm')} {
      img {
        width: 100%;
      }
    }
  `,

  cardContentFull: css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    height: 100%;

    ${breakpoints.down('sm')} {
      grid-template-columns: 1fr;
    }
  `,

  oneCardModuleBackgroundImageContainer: css`
    position: relative;
    pointer-events: none;
  `,

  oneCardModuleBackgroundImage: css`
    ${breakpoints.up('sm')} {
      position: absolute;
      bottom: 0;
      right: 0;
      height: 100%;
    }

    img {
      object-fit: contain;

      ${breakpoints.up('sm')} {
        height: 100%;
        width: 100%;
        object-position: right bottom;
      }

      ${breakpoints.down('xs')} {
        width: 100%;
      }
    }
  `,

  oneCardModuleContent: css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    height: 100%;

    ${breakpoints.down('xs')} {
      grid-template-columns: 1fr;
    }
  `,
};

export const CannedCollectionModule = React.forwardRef<HTMLDivElement, Props>((props: Props, ref) => {
  const {
    heading,
    subheading,
    cards,
    size,
    color,
    preloadImage,
    lazyLoadImage,
    showAllEntries,
    showImageInFull = false,
    variantConfig,
  } = props;
  const isRtl: boolean = isRightToLeft(_t.getLocale());
  const isMobile: boolean = useMediaQuery(breakpoints.down('xs'));
  const isXxs: boolean = useMediaQuery(breakpoints.down(XXS_BREAKPOINT));
  const isOneCardCannedCollectionModule: boolean = cards.length === 1;

  // If true, cards are displayed in grid view for all viewport widths
  const isGridVariant = (variantConfig?.sm === 'grid' || showImageInFull) && size === CARD_SIZE.LARGE;

  let cardSizeStyle = styles.cardSizeRegular;
  let cardTitleVariant: React.ComponentProps<typeof Typography2>['variant'] = 'subtitleLarge';
  let cardSubtitleVariant: React.ComponentProps<typeof Typography2>['variant'] = 'bodySecondary';

  switch (size) {
    case CARD_SIZE.LARGE:
      cardSizeStyle = styles.cardSizeLarge;
      cardSubtitleVariant = 'bodyPrimary';
      cardTitleVariant = 'subtitleLarge';
      break;
    case CARD_SIZE.REGULAR:
      cardSizeStyle = styles.cardSizeRegular;
      break;
    case CARD_SIZE.SMALL:
      cardTitleVariant = 'subtitleMedium';
      break;
    default:
      cardSizeStyle = styles.cardSizeSmall;
      break;
  }

  const cardColorStyle = (cardBackgroundColor?: ColorChoice) => {
    return `background-color:
    ${getColorFromColorName(cardBackgroundColor)};
  `;
  };

  const cardBorderStyle =
    color === 'white' ? `border: 1px solid var(--cds-color-neutral-disabled-strong);` : `border: none;`;

  const renderCardContent = (item: CannedCollectionModuleCard) => {
    let headingLinkEl: HTMLAnchorElement | null = null;
    const backgroundImage = item.cardBackgroundImage;
    const cardBackgroundColor = item.backgroundColor ?? color;
    const isDarkBackground = cardBackgroundColor ? isInvertedColor(cardBackgroundColor) : false;
    const shouldShowImageInFull = showImageInFull && backgroundImage?.url;
    const {
      variant: ctaVariant,
      iconColor: ctaIconColor,
      isGhostCta,
    } = getCtaConfig({
      ctaVariant: item.ctaVariant,
      isDarkBackground,
      isMobile,
      size,
    });

    const cardStyles = [
      styles.card,
      size === CARD_SIZE.LARGE && styles.largeCardMd,
      cardColorStyle(cardBackgroundColor),
      cardBorderStyle,
    ].filter(Boolean);

    const renderBackgroundImage = () => {
      if (!backgroundImage?.url) return null;

      if (isOneCardCannedCollectionModule) {
        return (
          <div css={styles.oneCardModuleBackgroundImageContainer}>
            <div css={styles.oneCardModuleBackgroundImage}>
              <ResponsiveImage
                images={[
                  {
                    url: item.cardBackgroundImage?.url,
                    width: item.cardBackgroundImage?.width,
                    height: item.cardBackgroundImage?.height,
                    alt: item.cardBackgroundImage?.description,
                    media: `(min-width: ${breakpoints.values.sm}px)`,
                    isFallback: true,
                  },
                  {
                    url: item.mobileCardBackgroundImage?.url,
                    width: item.mobileCardBackgroundImage?.width,
                    height: item.mobileCardBackgroundImage?.height,
                    alt: item.mobileCardBackgroundImage?.description,
                    media: `(max-width: ${breakpoints.values.sm - 1}px)`,
                  },
                ]}
                fetchPriority="auto"
              />
            </div>
          </div>
        );
      }

      if (shouldShowImageInFull) {
        return (
          <div css={styles.backgroundImageContainer}>
            <div css={styles.backgroundImageFull}>
              <Image {...backgroundImage} shouldPreload={preloadImage} shouldLazyLoad={lazyLoadImage} />
            </div>
          </div>
        );
      }

      // Default behavior
      return (
        <div css={styles.backgroundImage}>
          <div
            css={styles.backgroundImageInner(
              backgroundImage?.width
                ? Math.max(BACKGROUND_IMAGE_LEFT_OFFSET * 2 - backgroundImage?.width, BACKGROUND_IMAGE_LEFT_OFFSET)
                : BACKGROUND_IMAGE_LEFT_OFFSET,
              isRtl,
              isOneCardCannedCollectionModule
            )}
          >
            <Image
              {...backgroundImage}
              style={{
                maxWidth: 'unset',
              }}
              shouldPreload={preloadImage}
              shouldLazyLoad={lazyLoadImage}
            />
          </div>
        </div>
      );
    };

    const getContentStyles = () => {
      const base = shouldShowImageInFull
        ? [cardSizeStyle, styles.cardContent(false)]
        : [cardSizeStyle, styles.cardContent(!!backgroundImage?.url)];
      return item.target ? [...base, styles.cardInteractionProxy] : base;
    };

    const contents = (
      <div
        css={[
          styles.cardContentWrapper,
          !isOneCardCannedCollectionModule && shouldShowImageInFull && styles.cardContentFull,
          isOneCardCannedCollectionModule && styles.oneCardModuleContent,
        ]}
      >
        <div css={getContentStyles()}>
          <div>
            {(item.eyebrow || item.eyebrowImage) && (
              <div
                css={size === CARD_SIZE.LARGE ? styles.eyebrowSpacingLarge : styles.eyebrowSpacing}
                data-testid="eyebrow-image"
              >
                <Eyebrow
                  text={item.eyebrow}
                  background={item.eyebrowBackground}
                  image={item.eyebrowImage}
                  eyebrowImageHeight={item.eyebrowImageHeight}
                />
              </div>
            )}
            {item.headline && (
              <Typography2
                component="h3"
                variant={cardTitleVariant}
                css={size === CARD_SIZE.LARGE ? styles.cardTitleLarge : styles.cardTitle}
                color={isDarkBackground ? 'invertBody' : 'body'}
              >
                {item.target ? (
                  <span
                    ref={(el) => {
                      headingLinkEl = el?.querySelector('a') ?? null;
                    }}
                  >
                    <PreservedUtmLink
                      href={item.target}
                      openInNewTab={item.openInNewTab ?? true}
                      onClick={item.onClick}
                      trackingName="canned_collection_card"
                      css={[styles.stretchedOverlay, styles.headingLink]}
                      {...(item.ctaText ? { 'aria-describedby': `cta-${item.id}` } : {})}
                    >
                      {item.headline}
                    </PreservedUtmLink>
                  </span>
                ) : (
                  item.headline
                )}
              </Typography2>
            )}
            {item.body && (
              <Typography2
                variant={cardSubtitleVariant}
                component="div"
                color={isDarkBackground ? 'invertBody' : 'body'}
              >
                {item.body}
              </Typography2>
            )}
          </div>
          {item.ctaText && (
            <div>
              <Button
                css={[
                  styles.cta,
                  size === CARD_SIZE.LARGE && styles.ctaLarge,
                  isGhostCta && styles.ghostCta,
                  item.target && styles.decorativeCta,
                ]}
                size="small"
                variant={ctaVariant}
                icon={<ArrowNextIcon color={ctaIconColor} />}
                iconPosition="after"
                {...(item.target
                  ? {
                      'aria-hidden': true,
                      tabIndex: -1,
                      id: `cta-${item.id}`,
                      onClick: () => {
                        headingLinkEl?.click();
                      },
                    }
                  : {})}
              >
                {item.ctaText}
              </Button>
            </div>
          )}
        </div>
        {renderBackgroundImage()}
      </div>
    );

    return <BasicCard css={cardStyles}>{contents}</BasicCard>;
  };

  let numDisplayedRows = size === CARD_SIZE.SMALL ? 2 : 1;
  if (showAllEntries) {
    numDisplayedRows = cards.length;
  }

  /**
   * This is a workaround to handle multiple card layouts on mobile resolutions.
   * The CDS grid component has a breakpoint for XS but no generalized way to configure this
   * and no additional breakpoints for smaller screens. This is noticable on mobile devices.
   *
   * In order to get around this we populate the grid config with xxs as the default to handle
   * initial mobile rendering, and then break to the xxs -> xs resolution after first render.
   *
   * @returns GridConfig
   */
  const getItemsGridConfig = (): GridConfig => {
    let xsSize: GridConfig['xs'] = 12;
    if (isMobile && showImageInFull && !isXxs) {
      xsSize = isOneCardCannedCollectionModule ? 12 : 6;
    }

    if (isOneCardCannedCollectionModule) {
      return { xs: xsSize, sm: 12, md: 12 };
    } else if (isGridVariant) {
      return { xs: xsSize, sm: 6, md: 6 };
    } else if (size === CARD_SIZE.LARGE) {
      return { xs: xsSize, sm: 12, md: 6 };
    }

    return { xs: xsSize, sm: 6, md: 4 };
  };

  const contentPanel = () => {
    return (
      <div>
        {(heading || subheading) && (
          <div css={styles.headingContainer}>
            {heading && (
              <Typography2 variant="titleSmall" component="h2" color="inherit">
                {heading}
              </Typography2>
            )}
            {subheading && (
              <Typography2 variant="subtitleMedium" component="h3" color="inherit" css={styles.subheading}>
                {subheading}
              </Typography2>
            )}
          </div>
        )}
        <ShowMoreGridSection
          items={cards}
          spacing={isMobile ? 8 : 16}
          itemsGridConfig={getItemsGridConfig()}
          defaultRows={numDisplayedRows}
          renderItem={({ item }) => renderCardContent(item)}
          css={styles.showMoreGridSection}
        />
      </div>
    );
  };

  return <ModuleLayout assembly={noImage(props)} renderContent={contentPanel} ref={ref} />;
});

export default CannedCollectionModule;
